import { Injectable, inject } from '@angular/core';
import {map, skipWhile} from 'rxjs/operators';
import {BehaviorSubject, fromEvent, Observable, of, switchMap} from 'rxjs';
import {LoggerService} from './logger.service';

export enum MenuState {
	open = 'open',
	closed = 'closed'
}

/**
 * Provides an event bus for the application. `window` or `document` listeners should be defined here so that a single
 * Observable can supply values to interested Components.
 *
 * Do _not_ use `@HostListener('window:…')` or `@HostListener('document:…)` since this will create duplicate event
 * listeners for every Component that defines one, which will inevitably lead to performance issues.
 */
@Injectable({
	providedIn: 'root'
})
export class EventService {
	private logger = inject(LoggerService);


	// The PointerEvent from a click anywhere on the document
	documentClick = fromEvent(document, 'click') as Observable<PointerEvent>;

	// The target element of a click that occurred anywhere on the document
	documentClickTarget: Observable<HTMLElement | null> = this.documentClick.pipe(
		map(event => event.target as HTMLElement)
	);

	/**
	 * Emits a value anytime there is a click anywhere on the document, with return values indicating:
	 *   true: The click occurred within the specified target(s)
	 *   false: The click occurred outside the specified target(s)
	 *   undefined: A valid target was not supplied
	 * @param targets
	 * @deprecated Use Angular HostListener instead. See {@link PopoverComponent} for example.
	 */
	clickInside = (targets?: HTMLElement | Array<HTMLElement>): Observable<boolean | undefined> => this.documentClickTarget.pipe(
		map(eventTarget => {
			// this.logger.debug('clickInside eventTarget', eventTarget);
			return Array.isArray(targets)
				// If array of elements, check that at least 1 click was inside
				? targets.some(t => t.contains(eventTarget))
				: targets?.contains(eventTarget)
		})
	);

	/**
	 * Emits when a click event occurs outside the specified target.<br>
	 * <strong>Be sure to unsubscribe when you are done!</strong><br>
	 * Most use cases will involve `pipe(take(1))` as clickOutside is generally used for closing an open
	 * dialog/tooltip/panel/etc.
	 * @param target
	 * @deprecated Use Angular HostListener instead. See {@link PopoverComponent} for example.
	 */
	clickOutside = (target?: HTMLElement): Observable<void> => this.documentClickTarget.pipe(
		// click was inside?
		map(eventTarget => target?.contains(eventTarget)),
		skipWhile(clickInside => clickInside === true),
		switchMap(() => of(undefined))
	);

	private _menuState = new BehaviorSubject<MenuState>(MenuState.closed);
	setMenu = (val: MenuState) => this._menuState.next(val);
	menuState = this._menuState.asObservable();
}
